import { forwardRef, useId, useMemo } from "react";

/**
 * A living, breathing vortex node. Purely SVG so it composes into the
 * bodygraph and spiral engines. When `active` is true the node expands,
 * emits ripple rings, and rotates faster.
 *
 * Respect for reduced-motion is handled globally via `motion-safe:` classes
 * on the animated elements — reduced-motion users get a static gradient orb.
 */
export type EnergyNodeProps = {
  cx: number;
  cy: number;
  r?: number;
  hue?: number;           // 0-360, drives color harmonic
  label?: string;
  active?: boolean;
  defined?: boolean;      // when false, the node reads "open" — dimmer core
  onActivate?: () => void;
  spiral?: boolean;       // draw inner spiral (default true)
  ariaLabel?: string;
};

export const EnergyNode = forwardRef<SVGGElement, EnergyNodeProps>(function EnergyNode(
  { cx, cy, r = 30, hue = 44, label, active = false, defined = true, onActivate, spiral = true, ariaLabel },
  ref,
) {
  const uid = useId().replace(/:/g, "");
  const gradId = `en-grad-${uid}`;
  const coreColor = `hsl(${hue} 85% ${defined ? 62 : 40}%)`;
  const outerColor = `hsl(${hue} 85% 32% / 0.6)`;

  // Precompute spiral path — 2.25 turns, 80 samples
  const spiralPath = useMemo(() => {
    if (!spiral) return "";
    const turns = 2.25, maxR = r * 0.85, steps = 72;
    let d = "";
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const angle = t * turns * Math.PI * 2;
      const rr = t * maxR;
      const x = cx + Math.cos(angle) * rr;
      const y = cy + Math.sin(angle) * rr;
      d += (i === 0 ? "M" : "L") + x.toFixed(2) + "," + y.toFixed(2) + " ";
    }
    return d;
  }, [cx, cy, r, spiral]);

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onActivate?.(); }
  };

  return (
    <g
      ref={ref}
      role={onActivate ? "button" : undefined}
      tabIndex={onActivate ? 0 : undefined}
      aria-label={ariaLabel ?? label}
      onClick={onActivate}
      onKeyDown={onActivate ? handleKey : undefined}
      className={onActivate ? "gate-focus-ring cursor-pointer outline-none" : undefined}
      style={{ transformBox: "fill-box", transformOrigin: `${cx}px ${cy}px` }}
    >
      <defs>
        <radialGradient id={gradId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={coreColor} stopOpacity={defined ? 0.95 : 0.35} />
          <stop offset="60%" stopColor={coreColor} stopOpacity={defined ? 0.35 : 0.12} />
          <stop offset="100%" stopColor={outerColor} stopOpacity={0} />
        </radialGradient>
      </defs>

      {/* Ripple rings on activation (motion-safe only) */}
      {active && (
        <>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={coreColor} strokeOpacity={0.7}
            className="motion-safe:animate-[energy-ripple_1.8s_ease-out_infinite]" style={{ transformBox: "fill-box", transformOrigin: `${cx}px ${cy}px` }} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={coreColor} strokeOpacity={0.4}
            className="motion-safe:animate-[energy-ripple_1.8s_ease-out_0.6s_infinite]" style={{ transformBox: "fill-box", transformOrigin: `${cx}px ${cy}px` }} />
        </>
      )}

      {/* Glow halo (breathing) */}
      <circle
        cx={cx} cy={cy}
        r={r * (active ? 1.15 : 1)}
        fill={`url(#${gradId})`}
        className="motion-safe:animate-[energy-breath_5s_ease-in-out_infinite]"
        style={{ transformBox: "fill-box", transformOrigin: `${cx}px ${cy}px` }}
      />

      {/* Inner ring */}
      <circle cx={cx} cy={cy} r={r * 0.95} fill="none"
        stroke={coreColor} strokeOpacity={defined ? 0.55 : 0.22} strokeWidth={1} />

      {/* Spiral core */}
      {spiral && (
        <path d={spiralPath} fill="none"
          stroke={coreColor}
          strokeOpacity={defined ? (active ? 1 : 0.8) : 0.4}
          strokeWidth={active ? 1.8 : 1.2}
          strokeLinecap="round" strokeLinejoin="round"
          className="motion-safe:animate-[energy-rotate_28s_linear_infinite]"
          style={{ transformBox: "fill-box", transformOrigin: `${cx}px ${cy}px` }}
        />
      )}

      {label && (
        <text x={cx} y={cy + r + 14} textAnchor="middle"
          className="fill-gold-luminous pointer-events-none"
          style={{ fontSize: 10, letterSpacing: 1.4 }}>
          {label.toUpperCase()}
        </text>
      )}
    </g>
  );
});
